/**
 * 10.04.26: диалог прогресса для async массового approve-all.
 * Связи: вызывается из BroadcastQueueDialogs, получает job из useBroadcastQueuePage через WS/polling.
 */
import { Button } from './Button';
import { Modal } from './Modal';
import { ProgressBar } from './ProgressBar';
import { StatusBadge } from './StatusBadge';
import {
  type BroadcastOperationJob,
  broadcastOperationStatusLabels,
  channelDisplayNames,
} from '@/types/broadcastQueue';

interface OperationProgressDialogProps {
  isOpen: boolean;
  title: string;
  job: BroadcastOperationJob | null;
  isWebSocketConnected?: boolean;
  onClose: () => void;
}

function isTerminalStatus(status?: string) {
  return status === 'completed' || status === 'failed' || status === 'partial';
}

function mapStatusBadge(status?: string): 'active' | 'pending' | 'warning' | 'error' {
  switch (status) {
    case 'completed':
      return 'active';
    case 'partial':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'pending';
  }
}

export function OperationProgressDialog({
  isOpen,
  title,
  job,
  isWebSocketConnected = false,
  onClose,
}: OperationProgressDialogProps) {
  if (!isOpen || !job) {
    return null;
  }

  const isTerminal = isTerminalStatus(job.status);
  const currentTarget = job.current_patient_name || job.current_phone || 'Подготовка списка';

  return (
    <Modal
      isOpen={isOpen}
      onClose={isTerminal ? onClose : () => {}}
      title={title}
      size="lg"
      closeOnBackdropClick={isTerminal}
      footer={(
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-neutral-500">
            {isWebSocketConnected ? 'Realtime: WebSocket подключен' : 'Realtime недоступен, обновляем через polling'}
          </p>
          <Button onClick={onClose} disabled={!isTerminal}>
            {isTerminal ? 'Закрыть' : 'Выполняется...'}
          </Button>
        </div>
      )}
    >
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-neutral-500">Статус операции</p>
            <div className="mt-2">
              <StatusBadge
                status={mapStatusBadge(job.status)}
                activeText={broadcastOperationStatusLabels[job.status]}
                pendingText={broadcastOperationStatusLabels[job.status]}
                warningText={broadcastOperationStatusLabels[job.status]}
                errorText={broadcastOperationStatusLabels[job.status]}
                showIcon
              />
            </div>
          </div>
          <div className="text-right text-sm text-neutral-600">
            <p>Всего: {job.total_items}</p>
            <p>Отправлено: {job.sent_items}</p>
            <p>Ошибки: {job.failed_items}</p>
          </div>
        </div>

        <ProgressBar
          value={job.processed_items}
          max={Math.max(job.total_items, 1)}
          label={`${job.processed_items} из ${job.total_items}`}
          variant={job.status === 'failed' ? 'error' : job.status === 'completed' ? 'success' : 'default'}
          size="lg"
        />

        {(job.message || job.current_channel || job.current_phone || job.current_patient_name) && (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
            <p className="text-sm font-medium text-neutral-900">
              {job.message || `Сейчас: ${currentTarget}`}
            </p>
            {(job.current_channel || job.current_phone || job.current_patient_name) && (
              <p className="mt-1 text-sm text-neutral-600">
                {currentTarget}
                {job.current_channel ? ` · ${channelDisplayNames[job.current_channel] || job.current_channel}` : ''}
              </p>
            )}
            {job.last_error && (
              <p className="mt-2 text-sm text-rose-700">{job.last_error}</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border border-neutral-200 p-3">
            <p className="text-xs uppercase tracking-wide text-neutral-500">Обработано</p>
            <p className="mt-1 text-2xl font-semibold text-neutral-900">{job.processed_items}</p>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <p className="text-xs uppercase tracking-wide text-green-700">Ушли</p>
            <p className="mt-1 text-2xl font-semibold text-green-800">{job.sent_items}</p>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
            <p className="text-xs uppercase tracking-wide text-rose-700">Ошибки</p>
            <p className="mt-1 text-2xl font-semibold text-rose-800">{job.failed_items}</p>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-neutral-900">Последние обработанные</h3>
            <span className="text-xs text-neutral-500">{job.recent_items.length}</span>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-neutral-200">
            {job.recent_items.length === 0 ? (
              <div className="p-4 text-sm text-neutral-500">Список появится после первых обработанных сообщений.</div>
            ) : (
              <div className="divide-y divide-neutral-100">
                {job.recent_items.slice().reverse().map((item) => {
                  const itemLabel = item.patient_name || item.phone;
                  const itemStatus = item.status === 'failed' ? 'error' : 'active';
                  const statusLabel = item.status === 'failed' ? 'Ошибка' : 'Отправлено';
                  return (
                    <div key={`${item.queue_item_id}-${item.processed_at}`} className="flex items-start justify-between gap-3 p-3 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium text-neutral-900">{itemLabel}</p>
                        <p className="text-neutral-500">
                          {item.phone}
                          {item.channel ? ` · ${channelDisplayNames[item.channel] || item.channel}` : ''}
                        </p>
                        {item.error && <p className="mt-1 text-rose-700">{item.error}</p>}
                      </div>
                      <StatusBadge
                        status={itemStatus}
                        activeText={statusLabel}
                        errorText={statusLabel}
                        size="sm"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
